import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, TrendingUp, MapPin, Home } from "lucide-react";
import { capture } from "@/lib/analytics";

const Hero = () => {
  const { t } = useLanguage();
  const navigate = useLocaleNavigate();

  return (
    <section className="pt-16 bg-[hsl(210,20%,98%)]">
      <div className="container mx-auto px-4 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Content */}
          <div>
            <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-extrabold text-foreground mb-5 leading-[1.15] tracking-tight">
              {t.hero.title}
            </h1>
            <p className="text-base lg:text-lg text-muted-foreground max-w-lg mb-10 leading-relaxed">
              {t.hero.subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3 mb-4">
              <Button
                size="lg"
                className="px-8 h-14 rounded-full text-base gap-2"
                onClick={() => {
                  capture("click_start_free", { source: "hero" });
                  navigate("/properties");
                }}
              >
                {t.hero.ctaPrimary}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="px-8 h-14 rounded-full text-base"
                onClick={() => {
                  capture("click_pricing", { source: "hero" });
                  navigate("/pricing");
                }}
              >
                {t.hero.ctaSecondary}
              </Button>
            </div>

            {/* Trust line */}
            <p className="text-sm text-muted-foreground">
              {t.hero.trustLine}
            </p>

          </div>

          {/* Right: Mock Card / Hero Visual */}
          <div className="hidden lg:flex justify-center">
            <div className="relative w-full max-w-md">
              {/* Mock property card */}
              <div className="bg-card rounded-2xl border border-border shadow-xl p-5 space-y-4">
                <div className="aspect-[4/3] rounded-xl bg-muted overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&q=80"
                    alt="3D property illustration"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    {t.hero.mockCard.location}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-foreground">{t.hero.mockCard.price}</span>
                    <span className="text-sm text-muted-foreground">{t.hero.mockCard.area}</span>
                  </div>
                  <div className="text-sm text-muted-foreground">{t.hero.mockCard.pricePerSqm}</div>
                  <div className="pt-3 border-t border-border space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{t.hero.mockCard.aiValuation}</span>
                      <span className="text-sm font-bold text-green-600">{t.hero.mockCard.belowMarket}</span>
                    </div>
                    <div className="relative h-2 rounded-full bg-gradient-to-r from-green-500 via-yellow-400 to-red-500">
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-foreground border-2 border-background"
                        style={{ left: "25%" }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{t.hero.mockCard.marketScore}</span>
                      <div className="flex items-center gap-1">
                        <TrendingUp className="h-3 w-3 text-green-600" />
                        <span className="font-medium text-green-600">87/100</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/5 rounded-full blur-2xl" />
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-primary/5 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
